import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import DeepToast from "./DeepToast";
import SummaryPage from "./SummaryPage";
import ExitPage from "./ExitPage";
import CanvasInstructions from "./CanvasInstructions";
import GenerateQuestion from "./GenerateQuestion";
import WelcomePage from "./WelcomePage";
import TeamPage from "./TeamPage";
import HowItWorks from "./HowItWorks";
import Mission from "./Mission";
import AuthPage from "./AuthPage";
import OnboardingPage from "./onboarding";
import { supabase } from "./lib/supabase";
import { apiUrl, fetchJson } from "./api";
// import TxtToQtiAuto from "./qticonverter"; // disabled: component not exported

const RequireCanvasSetup = ({ session, children }) => {
  const [gate, setGate] = useState("loading");
  const userId = session?.user?.id;

  useEffect(() => {
    if (!userId) {
      setGate("need");
      return undefined;
    }
    let cancelled = false;
    const url = apiUrl(`/api/user/canvas-status?userId=${encodeURIComponent(userId)}`);
    fetchJson(url, {}, 15000)
      .then((d) => {
        if (!cancelled) setGate(d && d.hasToken ? "ready" : "need");
      })
      .catch(() => {
        // Backend unreachable or timeout — send user to onboarding instead of an infinite "Loading…"
        if (!cancelled) setGate("need");
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (gate === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary text-primary text-lg font-semibold">
        Loading...
      </div>
    );
  }
  if (gate === "need") {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
};

const ProtectedRoute = ({ session, children }) => {
  if (!session) {
    return <Navigate to="/auth" replace />;
  }
  return children;
};

const App = () => {
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    if (!supabase || !supabase.auth) {
      setAuthLoading(false);
      return undefined;
    }

    const getInitialSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (isMounted) {
        setSession(data.session);
        setAuthLoading(false);
      }
    };

    getInitialSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary text-primary text-lg font-semibold">
        Loading...
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<WelcomePage />} />
      <Route path="/auth" element={session ? <Navigate to="/onboarding" replace /> : <AuthPage />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute session={session}>
            <OnboardingPage session={session} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/deeptoast"
        element={
          <ProtectedRoute session={session}>
            <RequireCanvasSetup session={session}>
              <DeepToast />
            </RequireCanvasSetup>
          </ProtectedRoute>
        }
      />
      <Route
        path="/summary"
        element={
          <ProtectedRoute session={session}>
            <RequireCanvasSetup session={session}>
              <SummaryPage />
            </RequireCanvasSetup>
          </ProtectedRoute>
        }
      />
      <Route
        path="/generate-question"
        element={
          <ProtectedRoute session={session}>
            <RequireCanvasSetup session={session}>
              <GenerateQuestion />
            </RequireCanvasSetup>
          </ProtectedRoute>
        }
      />
      <Route
        path="/exit"
        element={
          <ProtectedRoute session={session}>
            <RequireCanvasSetup session={session}>
              <ExitPage />
            </RequireCanvasSetup>
          </ProtectedRoute>
        }
      />
      <Route
        path="/canvas-instructions"
        element={
          <ProtectedRoute session={session}>
            <RequireCanvasSetup session={session}>
              <CanvasInstructions />
            </RequireCanvasSetup>
          </ProtectedRoute>
        }
      />
      <Route path="/team" element={<TeamPage />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/mission" element={<Mission />} />
      {/*
        Route temporarily disabled because ./qticonverter does not export a React component.
        <Route path="/qticonverter" element={<TxtToQtiAuto />} />
      */}
    </Routes>
  );
};

export default App;
